import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { AlertColor } from '@mui/material';
import {
  GroupTypeDetails,
  StartEndSettings,
  StartEndSettingsInput,
  GroupType
} from 'graphql/types';
import { useMessage } from 'modules/intl';
import { EnabledDisabled } from './constant';
import { usePutStartEndSettings } from './hooks/usePutStartEndSettings';
import { useGetStartEndDefaultValues } from './hooks/useGetDefaultValues';
import { SettingBody } from './SettingBody';
import { startEndSettingUseFieldName } from './constant';
import { mapToAccessSope } from './utils';

type StartEndSettingsPageProps = {
  enabledGroupTypes: GroupTypeDetails[];
  settings: StartEndSettings[];
};

export const StartEndSettingsPage: React.FC<StartEndSettingsPageProps> = ({
  enabledGroupTypes,
  settings
}: StartEndSettingsPageProps) => {
  const [show, setShow] = useState<boolean>(false);
  const category = 'success' as AlertColor;
  const message = useMessage('settings.startEndSaveSuccessMessage');
  const fieldValues = useGetStartEndDefaultValues(settings);
  const [defaultValues, setDefaultValues] = useState<any>();
  const formMethods = useForm(fieldValues);
  const { control, reset, formState, getValues } = formMethods;
  const { isDirty, isSubmitSuccessful } = formState;
  const { fields, prepend, remove } = useFieldArray({
    control,
    name: startEndSettingUseFieldName
  });
  useEffect(() => {
    if (isSubmitSuccessful) {
      reset({ ...defaultValues });
    }
  }, [reset, defaultValues, isSubmitSuccessful]);
  useEffect(() => {
    reset(getValues());
  }, [getValues, reset]);
  const { putStartEndSettings, loading: saving } = usePutStartEndSettings();
  const onSubmit = useCallback(
    (formData) => {
      const saveSettings = async () => {
        let settings: StartEndSettingsInput[] = [];
        if (formData[startEndSettingUseFieldName]) {
          settings = formData[startEndSettingUseFieldName].map((r: any) => {
            return {
              enabled: r.enableDisabled === EnabledDisabled.enabled,
              accessScopeIn: {
                ...mapToAccessSope('departmentGroups', r.DEPARTMENT),
                ...mapToAccessSope('employeeTypeGroups', r.EMPLOYEE_TYPE),
                ...mapToAccessSope('locations', r.LOCATION),
                ...mapToAccessSope('costCenters', r.COST_CENTER),
                ...mapToAccessSope('divisions', r.DIVISION),
                ...mapToAccessSope('serviceCenters', r.SERVICE_CENTER)
              }
            };
          });
        }
        settings = settings.concat({
          enabled:
            formData.defaultRow.enableDisabled === EnabledDisabled.enabled
        });
        await putStartEndSettings({ settings: settings });
        setShow(false);
        setShow(true);
      };

      saveSettings();
      setDefaultValues(formData);
    },
    [putStartEndSettings]
  );

  const handleClick = useCallback(() => {
    if (enabledGroupTypes && enabledGroupTypes.length) {
      const values = enabledGroupTypes.reduce((a, c) => {
        a[c.value] = [];
        return a;
      }, {} as Record<GroupType, Array<any>>);
      prepend({
        ...values,
        ...{
          enableDisabled: EnabledDisabled.enabled
        }
      });
    }
  }, [enabledGroupTypes, prepend]);

  const handleRemove = useCallback(
    (rowId) => {
      remove(rowId);
    },
    [remove]
  );
  return (
    <FormProvider {...formMethods}>
      <form onSubmit={formMethods.handleSubmit(onSubmit)}>
        <SettingBody
          enabledGroupTypes={enabledGroupTypes}
          fields={fields}
          show={show}
          category={category}
          message={message}
          isDirty={isDirty}
          handleClick={handleClick}
          handleRemove={handleRemove}
          saving={saving}
          fieldName={startEndSettingUseFieldName}
          defaultRowValue={fieldValues.defaultValues.defaultEnabled}
        />
      </form>
    </FormProvider>
  );
};
